import React from 'react';
import { CheckIcon } from 'lucide-react';
import { SelectDropdown } from '../SelectDropdown';
import type { SurveyQuestion } from '../../types/survey';
interface SurveyQuestionFieldProps {
  question: SurveyQuestion;
  index: number;
  value: string;
  error?: string;
  onChange: (value: string) => void;
}
export function SurveyQuestionField({
  question,
  index,
  value,
  error,
  onChange
}: SurveyQuestionFieldProps) {
  const fieldId = `question-${question.id}`;
  const errorId = `${fieldId}-error`;
  const answered = value.trim().length > 0;
  return <div className="flex gap-3 px-4 py-6 sm:gap-5 sm:px-6 sm:py-7" data-id="element-2199" data-mp="2zupcec10mz61">
      <div aria-hidden="true" className={`mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-full text-xs font-semibold transition-colors duration-150 ${answered ? 'bg-[#00638C] text-white' : 'bg-gray-100 text-gray-500 ring-1 ring-inset ring-gray-200'}`} data-id="element-2200" data-mp="2l51gjqblsspp">
        {answered ? <CheckIcon size={14} strokeWidth={3} data-id="element-2201" data-mp="3p4dhi5e9qp0m" /> : index + 1}
      </div>

      <div className="min-w-0 flex-1" data-id="element-2202" data-mp="3p6q9lbdqqfre">
        <label htmlFor={question.type === 'choice' || question.type === 'scale' ? undefined : fieldId} id={question.type === 'choice' || question.type === 'scale' ? `${fieldId}-label` : undefined} className="block text-base font-semibold leading-snug text-gray-900" data-id="element-2203" data-mp="3l3l91ed80o5e">
          {question.prompt}
          {question.required && <span className="ml-1 text-[#DB3725]" aria-hidden="true" data-id="element-2204" data-mp="3g3yu90hquzuh">
              *
            </span>}
          {!question.required && <span className="ml-2 align-middle text-xs font-medium text-[#3A4F59]" data-id="element-2205" data-mp="3bcgugfrgj5b3">
              Optional
            </span>}
        </label>

        <div className="mt-3" data-id="element-2206" data-mp="2iol21n8x7zk4">
          {question.type === 'select' && <div className="max-w-lg" data-id="element-2207" data-mp="2imgwl0vdishq">
              <SelectDropdown fullWidth ariaLabel={question.prompt} value={value} options={(question.options ?? []).map(option => ({
            value: option,
            label: option
          }))} onChange={onChange} data-id="element-2208" data-mp="mlyf1rimn3t3" />
            </div>}

          {question.type === 'choice' && <div role="radiogroup" aria-labelledby={`${fieldId}-label`} className="flex flex-col gap-2 sm:max-w-lg" data-id="element-2209" data-mp="3nm3jz48thuc">
              {question.options?.map(option => {
            const selected = value === option;
            return <label key={option} className={`flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 text-sm transition-colors duration-150 ${selected ? 'border-[#60A5FA] bg-[#EFF6FF] font-medium text-[#004F70]' : 'border-gray-200 bg-[#F9FAFB] text-gray-900 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-2210" data-mp="hneotex035ux">
                    <input type="radio" name={fieldId} value={option} checked={selected} onChange={() => onChange(option)} className="h-4 w-4 flex-none border-gray-300 text-[#00638C] focus:ring-[#30718D]" data-id="element-2211" data-mp="1tfe3y24ilqq5" />
                    {option}
                  </label>;
          })}
            </div>}

          {question.type === 'scale' && question.scale && <div role="radiogroup" aria-labelledby={`${fieldId}-label`} className="flex flex-wrap items-center gap-x-3 gap-y-2" data-id="element-2212" data-mp="16ghuj3xqgx47">
              <span className="text-sm text-[#3A4F59]" data-id="element-2213" data-mp="30sxuxo3ulh15">
                {question.scale.minLabel}
              </span>
              {Array.from({
            length: question.scale.max - question.scale.min + 1
          }, (_, offset) => String((question.scale?.min ?? 1) + offset)).map(point => {
            const selected = value === point;
            return <label key={point} className={`flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border text-sm transition-colors duration-150 focus-within:ring-2 focus-within:ring-[#30718D] focus-within:ring-offset-2 ${selected ? 'border-[#00638C] bg-[#00638C] font-semibold text-white' : 'border-gray-200 bg-white text-gray-700 hover:border-[#60A5FA] hover:bg-[#EFF6FF]'}`} data-id="element-2214" data-mp="32ze1y2u5dpuf">
                    <input type="radio" name={fieldId} value={point} checked={selected} onChange={() => onChange(point)} className="sr-only" data-id="element-2215" data-mp="2b6eo5n5ec7nn" />
                    {point}
                    <span className="sr-only" data-id="element-2216" data-mp="20lt40l0xpj7t">
                      {` — ${point === String(question.scale?.min) ? question.scale?.minLabel : ''}${point === String(question.scale?.max) ? question.scale?.maxLabel : ''}`}
                    </span>
                  </label>;
          })}
              <span className="text-sm text-[#3A4F59]" data-id="element-2217" data-mp="25jl78b714rrb">
                {question.scale.maxLabel}
              </span>
            </div>}

          {question.type === 'longText' && <textarea id={fieldId} value={value} onChange={event => onChange(event.target.value)} placeholder={question.placeholder} rows={4} aria-invalid={Boolean(error)} aria-describedby={error ? errorId : undefined} className={`w-full resize-y rounded-lg border bg-white px-4 py-3 text-sm leading-relaxed text-gray-900 placeholder:text-gray-400 transition-colors duration-150 focus:outline-none focus:ring-2 ${error ? 'border-[#DB3725] focus:border-[#DB3725] focus:ring-[#DB3725]/30' : 'border-gray-300 focus:border-[#30718D] focus:ring-[#30718D]/30'}`} data-id="element-2218" data-mp="2fb8vk38ahsok" />}
        </div>

        {error && <p id={errorId} role="alert" className="mt-2 text-sm font-medium text-[#DB3725]" data-id="element-2219" data-mp="29l7gt1yaxp89">
            {error}
          </p>}
      </div>
    </div>;
}